/**
 * The console's credential core (U-08-f; plan D1, D9, D10).
 *
 * This is the browser's half of the human session: `POST /ops/v1/sessions` hands back a
 * short-lived session JWT, this module holds it, and every later door is authorised with it.
 * Plan D1 is the ruling that makes this the right shape — the session path IS the console's
 * token broker, because the assertion a broker was written for is a MACHINE credential that
 * must never reach a browser (`AGENTS.md` invariant ۴), and a BFF would have meant keeping the
 * service's private key in the request path of a UI. So: no signing here, no key material here,
 * no assertion here — only a session token.
 *
 * Four rules the rest of the console depends on:
 *
 *  1. **The token lives in module memory and nowhere else.** Not in Web Storage — neither the
 *     persistent nor the per-tab flavour — not in a cookie, not in the URL, not in a query
 *     parameter, and never printed to the developer console. (The structural guard
 *     `test/noBrowserCredentialStore.test.ts` is what enforces this list; this file names the
 *     rule without spelling the forbidden globals, so the guard never has to exempt it.)
 *     A page reload therefore means signing in again, and that is deliberate: a credential that
 *     survives a server-side revocation is the exact bug §۵ of the RBAC doc exists to prevent.
 *  2. **A 401 ends the session.** The façade answers a broken, revoked, expired or
 *     disabled-account token with 401 `signature-invalid`, so a 401 is the only signal a browser
 *     can get; `noteUnauthorized()` clears state and fires the signed-out event, which is what
 *     makes revocation visible instead of looking like a healthy page.
 *  3. **Levels are a mirror.** `levels` is exactly `effective.capabilities`, and `null` before
 *     that answer arrives. §۰/§۹/§۱۰: the console never produces an access rule, so nothing here
 *     consults a role, a navigation entry or a default. U-06-e's «صفر جای `null` نشیند» is why
 *     `levelsKnown` is a field rather than an empty map — «نمی‌دانم» and «نداری» are different
 *     sentences, and `useCapability()` has to be able to tell them apart.
 *  4. **A password is a parameter, never a value that is kept.** It is handed to the one door
 *     that accepts it and dropped: not in the token store, not in the snapshot a component
 *     renders, not in an error message.
 */

import type { TokenProvider } from './client';
import type { ConsoleApi } from './consoleApi';
import { ApiError } from './errors';
import { sessionLogoutIdempotencyKey } from '../lib/idempotency';
import type { CapabilityWire, EffectivePermissions, SessionGranted } from './types';

/** Why the console stopped being signed in; the login screen says it in one sentence. */
export type SignedOutReason = 'signed-out' | 'unauthorized';

/** Who is signed in, as the server described it. Carries no token and no password. */
export interface SessionIdentity {
  username: string;
  role: string;
  sid: string;
  expiresAt: string;
}

/**
 * The only view components may render.
 *
 * `levelsKnown` is the honesty field: `false` means `permissions/effective` has not answered,
 * which is NOT the claim «this caller holds nothing». `useCapability()` reads it.
 */
export interface SessionSnapshot {
  status: 'anonymous' | 'signed-in';
  identity: SessionIdentity | null;
  /** The server's own document, kept so a page can name the role vocabulary it renders. */
  effective: EffectivePermissions | null;
  levels: Record<string, CapabilityWire> | null;
  levelsKnown: boolean;
  /** Set when the session ended; the login screen shows it once and clears it on the next try. */
  signedOutReason: SignedOutReason | null;
}

const ANONYMOUS: SessionSnapshot = {
  status: 'anonymous',
  identity: null,
  effective: null,
  levels: null,
  levelsKnown: false,
  signedOutReason: null,
};

/** The provider whose three identity doors this session speaks through. */
let boundApi: ConsoleApi | null = null;
/** The ONLY place the bearer exists. */
let token: string | null = null;
/**
 * The username whose password step is waiting on a second factor, and nothing else about it.
 *
 * It is a label, not a credential: the challenge itself names the account server-side, so the
 * console never has to send a username with the code. Cleared the moment a session is adopted, a
 * local sign-out happens, or the app is reset.
 */
let challengeSubject: string | null = null;
let identity: SessionIdentity | null = null;
let effectiveView: EffectivePermissions | null = null;
let signedOutReason: SignedOutReason | null = null;
let snapshot: SessionSnapshot = ANONYMOUS;

/**
 * Bumped by every local clear, so an `effective` answer that arrives after a sign-out (or after
 * a 401) is discarded rather than populating a session that no longer exists.
 */
let generation = 0;

const stateListeners = new Set<() => void>();
const signedOutListeners = new Set<(reason: SignedOutReason) => void>();

/**
 * Point the core at the provider whose identity doors it should call.
 *
 * `createProvider()` and `<ConsoleApiProvider>` are the callers, and the reason this is a binding
 * rather than an argument on every function is the brief's own interface: the login form and the
 * identity badge call `signIn(username, password)` without holding a transport.
 */
export function bindSessionApi(api: ConsoleApi): void {
  boundApi = api;
}

function requireApi(): ConsoleApi {
  if (!boundApi) {
    throw new ApiError(
      'contract',
      'no data provider is bound to the session store — build the app through createProvider()',
    );
  }
  return boundApi;
}

function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

function publish(): void {
  snapshot = {
    status: identity === null ? 'anonymous' : 'signed-in',
    identity,
    effective: effectiveView,
    levels: effectiveView === null ? null : effectiveView.capabilities,
    levelsKnown: effectiveView !== null,
    signedOutReason,
  };
  for (const listener of [...stateListeners]) listener();
}

function clearLocal(reason: SignedOutReason): void {
  generation += 1;
  token = null;
  identity = null;
  effectiveView = null;
  challengeSubject = null;
  signedOutReason = reason;
  publish();
  for (const listener of [...signedOutListeners]) listener(reason);
}

/** A password step that is not finished yet: the server's challenge, and when it stops working. */
export interface SecondFactorChallenge {
  challenge: string;
  expiresAt: string | null;
}

/** What `POST /ops/v1/sessions` means: a session, or the challenge that still stands in its way. */
export type SignInStep =
  | { step: 'session'; identity: SessionIdentity }
  | { step: 'second-factor'; challenge: SecondFactorChallenge };

/**
 * Log a person in: `POST /ops/v1/sessions`, then read the mirror.
 *
 * The levels are fetched from `permissions/effective` even though the login response carries a
 * `capabilities` field too: §۹ names that door as the console's only source, and letting a second
 * document feed the same rendering is how a mirror becomes a policy engine. `session.test.ts`
 * proves the choice by making the two answers disagree.
 *
 * Rejects with the provider's own `ApiError`, which is how the login screen can tell the three
 * refusals apart: 401 `signature-invalid` (unknown user OR wrong password — one sentence, §۶),
 * 503 `auth-unconfigured` (nobody seeded an owner), 429 `rate-limited` with `retryAfterSeconds`.
 * A sign-in that cannot answer «چه چیزی می‌توانم ببینم» does not become a half-open session: the
 * token is dropped and the error propagates.
 *
 * **R8: this is the first step, and sometimes the only one.** An account with an enrolled second
 * factor is answered with `mfa_required` and a challenge instead of a token, so this function
 * returns `{ step: 'second-factor' }` having stored NOTHING — no token, no identity, no mirror.
 * That is the whole point of the shape: while the second factor is outstanding there is no session
 * in existence for this browser to hold.
 */
export async function beginSignIn(username: string, password: string): Promise<SignInStep> {
  const granted: SessionGranted = await requireApi().openSession({ username, password });
  if (granted.mfa_required === true) {
    const challenge = granted.challenge ?? '';
    if (challenge.length === 0) {
      // A challenge-shaped answer with nothing to spend would leave the page on a step no code can
      // ever finish. Naming that as a contract break beats rendering a dead form.
      throw new ApiError(
        'contract',
        'the login answer asked for a second factor but carried no challenge',
      );
    }
    challengeSubject = username;
    return {
      step: 'second-factor',
      challenge: { challenge, expiresAt: granted.challenge_expires_at ?? null },
    };
  }
  if (granted.token.length === 0) {
    // The façade serialises an empty token on the challenge answer; an empty token on a
    // NON-challenge answer is neither a session nor a step, and `Bearer ` must not become one.
    throw new ApiError(
      'contract',
      'the login answer carried neither a session token nor a second-factor challenge',
    );
  }
  challengeSubject = null;
  return { step: 'session', identity: await adoptGranted(granted, username) };
}

/**
 * The password-only face of `beginSignIn`, kept for every caller that has no use for a second step.
 *
 * An enrolled account cannot be signed in through here: the challenge is not a session, and
 * pretending otherwise would be the half-open login this store exists to prevent. Such a caller
 * gets a contract error pointing at `beginSignIn`/`submitSecondFactor`.
 */
export async function signIn(username: string, password: string): Promise<SessionIdentity> {
  const step = await beginSignIn(username, password);
  if (step.step === 'second-factor') {
    throw new ApiError(
      'contract',
      'this account has a second factor outstanding — the login is `beginSignIn` then `submitSecondFactor`',
    );
  }
  return step.identity;
}

/**
 * The second step: `POST /ops/v1/sessions/totp` with the challenge and the six digits.
 *
 * Only a code the server accepted produces a session — the row and its token are minted there,
 * after the code was verified — so the token is stored here, in module memory, exactly where the
 * one-step login stores it. A refusal (unknown/expired/spent challenge, wrong code, too many
 * attempts) is the door's one 401 `signature-invalid` and propagates untouched: the page shows the
 * server's sentence and keeps the operator on this step.
 */
export async function submitSecondFactor(
  challenge: string,
  code: string,
): Promise<SessionIdentity> {
  if (challengeSubject === null) {
    // The username is not sent (the challenge names it server-side); it is only the label the
    // identity badge uses until `permissions/effective` answers. With no password step in this
    // tab there is no such label, which means this call did not follow the step it belongs to.
    throw new ApiError(
      'contract',
      'no second-factor step is pending in this tab — call beginSignIn first',
    );
  }
  const username = challengeSubject;
  const granted: SessionGranted = await requireApi().submitTotp({ challenge, code });
  if (granted.mfa_required === true || granted.token.length === 0) {
    throw new ApiError('contract', 'the second-factor answer carried no session token');
  }
  challengeSubject = null;
  return adoptGranted(granted, username);
}

/** Hold the one credential and read the mirror — shared by both steps, so they cannot drift. */
async function adoptGranted(granted: SessionGranted, username: string): Promise<SessionIdentity> {
  const api = requireApi();
  token = granted.token;
  const who: SessionIdentity = {
    // `SessionGranted` publishes no username field, so this is the name the server just
    // authenticated; `effective().subject` is the server's copy of it and the badge prefers that.
    username,
    role: granted.role,
    sid: granted.sid,
    expiresAt: granted.expires_at,
  };
  identity = who;
  signedOutReason = null;
  generation += 1;
  const mine = generation;
  publish();

  try {
    const view = await api.effectivePermissions();
    // A sign-out, a 401 or a second login during this await makes the answer stale.
    if (mine === generation) {
      effectiveView = view;
      publish();
    }
  } catch (error) {
    if (mine === generation) {
      clearLocal(isUnauthorized(error) ? 'unauthorized' : 'signed-out');
    }
    throw error;
  }
  return who;
}

/**
 * Log out: revoke the server's session record, then forget the token.
 *
 * The order is the design — a JWT cannot be taken back before it expires, which is why the store
 * keeps a session table at all (§۵). A 401 is swallowed, because a session the server already
 * refused IS logged out and firing a second announcement would claim a second revocation. Any
 * other failure propagates with the local session intact: the token still works, and quietly
 * dropping it would hide exactly that.
 */
export async function signOut(): Promise<void> {
  if (identity === null) return;
  const api = requireApi();
  try {
    await api.closeSession({ idempotencyKey: sessionLogoutIdempotencyKey(identity.sid) });
  } catch (error) {
    if (!isUnauthorized(error)) throw error;
  }
  clearLocal('signed-out');
}

/**
 * The `TokenProvider` seam of `createHttpProvider`: one token per request, never a key.
 *
 * It rejects when there is no session rather than returning an empty string, which is what makes
 * a mis-wired provider fail in the console instead of sending `Bearer ` and letting the server
 * answer for it.
 */
export function tokenProvider(): TokenProvider {
  return async () => {
    if (token === null) {
      throw new ApiError('contract', 'no console session — sign in first');
    }
    return token;
  };
}

/** Called by the HTTP provider for a 401 that carried this session's bearer. */
export function noteUnauthorized(): void {
  if (identity === null) return;
  clearLocal('unauthorized');
}

/** Re-read the mirror (the matrix moved, or a page wants the server's latest answer). */
export async function refreshEffective(): Promise<EffectivePermissions | null> {
  if (identity === null) return null;
  const api = requireApi();
  const mine = generation;
  const view = await api.effectivePermissions();
  if (mine !== generation) return null;
  effectiveView = view;
  publish();
  return view;
}

/**
 * Publish a mirror that no door was asked for — fixture mode and tests only.
 *
 * Fixture mode has no server to ask, and the demo path (plus every page test that mounts a shell
 * with nobody signed in) needs the stand-in answer `web/src/fixtures/session.ts` holds, which is a
 * row of the server's own `_DEFAULT_LEVELS` table. Without this the console's default demo would
 * render «نمی‌دانم» on all 11 pages, i.e. the fixture would stop demoing the pages it exists to demo.
 *
 * Two limits, both deliberate: it publishes NO credential (a fixture has no bearer to hold, so
 * `status` stays `anonymous` and `tokenProvider()` still refuses), and a real sign-in always wins —
 * `signIn` overwrites the mirror with the server's answer. Test code forges an `effective` document
 * through here, which is what makes «the UI is a mirror» falsifiable rather than asserted.
 */
export function seedMirror(view: EffectivePermissions): void {
  if (token !== null) return;
  generation += 1;
  effectiveView = view;
  publish();
}

/** Drop a seeded mirror without touching anything else (the `unknown` state of the demo). */
export function clearMirror(): void {
  if (token !== null) return;
  generation += 1;
  effectiveView = null;
  signedOutReason = null;
  publish();
}

export function currentSession(): SessionIdentity | null {
  return identity;
}

/** The server's document, or `null` while it has not arrived (never a guess). */
export function effective(): EffectivePermissions | null {
  return effectiveView;
}

/** Stable object identity between changes — safe as a `useSyncExternalStore` snapshot. */
export function getSnapshot(): SessionSnapshot {
  return snapshot;
}

export function subscribe(listener: () => void): () => void {
  stateListeners.add(listener);
  return () => stateListeners.delete(listener);
}

/** «شما خارج شدید», with the reason. Returns an unsubscribe function. */
export function onSignedOut(listener: (reason: SignedOutReason) => void): () => void {
  signedOutListeners.add(listener);
  return () => signedOutListeners.delete(listener);
}

/** Test seam: forget everything, as a page reload does on its own. */
export function resetSession(): void {
  boundApi = null;
  token = null;
  identity = null;
  effectiveView = null;
  signedOutReason = null;
  challengeSubject = null;
  generation += 1;
  publish();
}
